import { FARBE_ZUSTAND_FALLBACK, TEXT_FLAECHEN, TEXT_KONTRAST_MIN } from '@/theme';
import { kontrast } from '@/lib/kontrast';

// ---------------------------------------------------------------------
// E61 — Die Zustandsfarbe als Schrift.
//
// Die Farbe eines Zustands steht in der Datenbank (`zustaende.farbe`) und
// ist Betriebsdaten, nicht Code (Designprinzip 2). Im Zustands-Chip ist
// sie Schrift- und Randfarbe. Eine zu helle Farbe macht den Chip
// unlesbar — der alte Standard für „Neu" (#D6E7E6) hatte 1,02 : 1 auf
// dem Tabellenkopf.
//
// WARUM DIE OBERFLÄCHE ABDUNKELT UND NICHT DER SERVER ABLEHNT
//   Es gibt keinen Endpunkt, der eine Zustandsfarbe speichert; geändert
//   wird sie nur per SQL oder Migration. Ablehnen oder Warnen hätte also
//   keinen Ort. Und die Schranke hängt an den Flächen, auf denen der
//   Text steht — die kennt nur die Oberfläche (TEXT_FLAECHEN in
//   theme.ts). Im Server müsste dieselbe Liste ein zweites Mal stehen.
//   Das ist keine Zuordnung im Sinn von Fallstrick 15: Hier wird nichts
//   einem Code zugeordnet, sondern ein Wert aus der Datenbank nach einer
//   festen Rechnung dargestellt.
//
// DIE REGEL
//   Farbton und Sättigung (HSL) bleiben, die Helligkeit sinkt in ganzen
//   Prozentpunkten, bis die Farbe auf allen vier Flächen 4,5 : 1 hält.
//   Eine Farbe, die schon trägt, kommt unverändert zurück. Migration 042
//   hat die Standardfarben nach genau dieser Regel umgeschrieben
//   (zustandFarbe.test.ts sichert, dass beides übereinstimmt).
// ---------------------------------------------------------------------

type Hsl = { h: number; s: number; l: number };

function zuHsl(hex: string): Hsl {
  const t = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(t.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

function ausHsl({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return (
    '#' +
    [r, g, b]
      .map((v) =>
        Math.round((v + m) * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
      .toUpperCase()
  );
}

/** Der kleinste Kontrast dieser Farbe über alle Textflächen. */
export function kleinsterKontrast(hex: string): number {
  return Math.min(...Object.values(TEXT_FLAECHEN).map((f) => kontrast(hex, f)));
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Die Farbe, in der ein Zustand als Schrift erscheint.
 *
 * `null` oder ein unlesbarer Wert ergibt das Rückfall-Grau — wie bisher.
 */
export function zustandTextfarbe(farbe: string | null | undefined): string {
  if (farbe == null || !HEX.test(farbe)) return FARBE_ZUSTAND_FALLBACK;
  if (kleinsterKontrast(farbe) >= TEXT_KONTRAST_MIN) return farbe;
  const hsl = zuHsl(farbe);
  for (let l = Math.round(hsl.l * 100) - 1; l >= 0; l--) {
    const kandidat = ausHsl({ ...hsl, l: l / 100 });
    if (kleinsterKontrast(kandidat) >= TEXT_KONTRAST_MIN) return kandidat;
  }
  return FARBE_ZUSTAND_FALLBACK;
}
